import { Select, Spin } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { formatMassDateTime } from '../../AddMassOffering/AddMassOfferingUtils';
import { useFetchDevotions } from '../../../api/actions/useFetchDevotions';
import selectors from '../../../store/selectors';

import styles from './DevotionOfferingDateSelector.module.scss';

const isDevotionAvailableForOrder = devotion => {
  const now = moment();
  const isDevotionDateInFuture = moment(devotion.massDate).isAfter(now) || !devotion.hasHour;
  return (
    (devotion.availableMassOfferings > 0 || devotion.hasUnlimitedOfferings) &&
    isDevotionDateInFuture &&
    !devotion.unavailableBecauseOfMinimumFromDate &&
    !devotion.unavailableBecauseOfEvenDistribution
  );
};

const DevotionOfferingDateSelector = ({
  fieldName,
  onChange,
  disabled,
  fieldValue,
  devotionType,
}) => {
  const timeZone = useSelector(selectors.selectTimeZone);
  const massOfferingMassRequestMinDate = useSelector(selectors.getMassOfferingMassRequestFromDate);
  const shouldShowMinDateDescription =
    massOfferingMassRequestMinDate &&
    moment(massOfferingMassRequestMinDate).isSameOrAfter(moment().startOf('day').add(1, 'day'));
  const {
    payload: devotions,
    loading: isFetchingDevotions,
    refetch,
  } = useFetchDevotions(devotionType);

  // Reset selected value and refetch devotions when devotionType changes
  useEffect(() => {
    if (onChange) {
      onChange(''); // Reset selected value
    }
    refetch(); // Refetch devotions for new type
  }, [devotionType]);

  // Select first available devotion when data is loaded and no value is selected
  useEffect(() => {
    if (!isFetchingDevotions && devotions?.length > 0 && !fieldValue && onChange) {
      const firstAvailableDevotionId = devotions.find(isDevotionAvailableForOrder)?.id;
      if (firstAvailableDevotionId) {
        onChange(firstAvailableDevotionId.toString());
      }
    }
  }, [devotions, isFetchingDevotions, fieldValue, onChange]);

  return (
    <>
      {shouldShowMinDateDescription && (
        <p>
          Przez formularz online można zamówić intencje na nabożeństwa odprawiane od{' '}
          <b>{moment(massOfferingMassRequestMinDate).format('DD.MM.YYYY')}</b>. W&nbsp;przypadku
          innej potrzeby, prosimy o&nbsp;bezpośredni kontakt z&nbsp;kancelarią.
        </p>
      )}
      {isFetchingDevotions ? (
        <Spin />
      ) : (
        <Select
          placeholder="Wybierz termin"
          disabled={disabled}
          name={fieldName}
          variant="outlined"
          onChange={newDevotionId => {
            if (onChange) {
              onChange(newDevotionId);
            }
          }}
          value={fieldValue}
          className={styles.select}
          notFoundContent="Brak dostępnych terminów"
        >
          {devotions &&
            devotions.map(devotion => {
              const massDate = formatMassDateTime(devotion, timeZone);
              const isAvailableForOrder = isDevotionAvailableForOrder(devotion);
              let disabledReason = ' brak wolnych intencji';
              if (devotion.unavailableBecauseOfMinimumFromDate) {
                disabledReason = ' niedostępne online w tym terminie';
              } else if (devotion.unavailableBecauseOfEvenDistribution) {
                disabledReason = ' tymczasowo niedostępne online';
              }

              return (
                <Select.Option
                  key={devotion.id}
                  value={devotion.id}
                  disabled={!isAvailableForOrder}
                >
                  {massDate}
                  {!isAvailableForOrder && (
                    <div className={styles.disabledReason}>{disabledReason}</div>
                  )}
                </Select.Option>
              );
            })}
        </Select>
      )}
    </>
  );
};

DevotionOfferingDateSelector.propTypes = {
  disabled: PropTypes.bool,
  fieldName: PropTypes.string.isRequired,
  fieldValue: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  devotionType: PropTypes.string.isRequired,
};

export default DevotionOfferingDateSelector;
